/**
 * Analytics tracking for menu interactions
 */

import type { ServiceId } from '@/config/servicesMenu';
import type { SwissLanguage } from '@/utils/languageTypes';

export interface MenuClickEvent {
  location: 'header' | 'mobile' | 'footer';
  service_id: ServiceId;
  locale: SwissLanguage;
  group?: string;
  has_children?: boolean;
}

export interface CityQuickLinkEvent {
  service_id: ServiceId;
  city: string;
  locale: SwissLanguage;
}

/**
 * Track menu click events
 */
export const trackMenuClick = (event: MenuClickEvent) => {
  // GA4 event
  if (typeof gtag !== 'undefined') {
    gtag('event', 'menu_click', {
      menu_location: event.location,
      service_id: event.service_id,
      language: event.locale,
      service_group: event.group,
      has_submenu: event.has_children || false,
      custom_parameters: {
        click_type: 'service_navigation',
        navigation_level: event.has_children ? 'parent' : 'leaf'
      }
    });
  }

  // Console log for development
  if (import.meta.env.DEV) {
    console.log('Menu Click:', event);
  }
};

/**
 * Track city quick link clicks
 */
export const trackCityQuickLink = (event: CityQuickLinkEvent) => {
  // GA4 event
  if (typeof gtag !== 'undefined') {
    gtag('event', 'city_quick_link_click', {
      service_id: event.service_id,
      city: event.city,
      language: event.locale,
      custom_parameters: {
        click_type: 'city_navigation',
        intent: 'local_service_access'
      }
    });
  }

  // Console log for development
  if (import.meta.env.DEV) {
    console.log('City Quick Link:', event);
  }
};

/**
 * Track menu open/close events
 */
export const trackMenuToggle = (action: 'open' | 'close', location: 'header' | 'mobile') => {
  if (typeof gtag !== 'undefined') {
    gtag('event', 'menu_toggle', {
      action,
      menu_location: location,
      custom_parameters: {
        interaction_type: 'menu_navigation'
      }
    });
  }
};

/**
 * Track service group interactions (accordion expand/collapse)
 */
export const trackServiceGroupToggle = (group: string, action: 'expand' | 'collapse', locale: SwissLanguage) => {
  if (typeof gtag !== 'undefined') {
    gtag('event', 'service_group_toggle', {
      service_group: group,
      action,
      language: locale,
      custom_parameters: {
        interaction_type: 'group_navigation'
      }
    });
  }
};